import type { Experiment } from 'types';
import type { PlanningBaseline } from '@@/api/statistics/planning/planning-baseline';
import type { PlanAssumptions } from 'helpers/plan-assumptions';
import type { PlanEvaluation } from 'helpers/experiment-plan';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { resolveObjectives } from 'helpers/objectives';
import { resolveWeights } from 'helpers/weight-distribution';
import { parsePlanAssumptions } from 'helpers/plan-assumptions';
import { config } from 'config';
import { useVariants } from './useVariants';
import { useExperiment } from './useExperiment';
import { evaluatePlan } from 'helpers/experiment-plan';

/** A 5% relative lift: the same default the running power panel sizes for with no signal. */
export const DEFAULT_PLAN: PlanAssumptions = { liftPct: 5, dailySessions: null, siteSharePct: null, ratePct: null };

export type { PlanEvaluation };

/**
 * An experiment's run-length plan: saved assumptions over the website's history.
 *
 * What is saved (experiments.plan) is the three assumptions, never the results: the
 * required sample and length are recomputed here every time, so they cannot go stale
 * against the power maths or the variant split.
 *
 * Nothing is estimated before someone plans: like PXL, an unplanned experiment has no
 * numbers until the wizard is completed. The site's figures are fetched only while the
 * wizard is open (its options offer them) or for a saved plan that uses them.
 */
type UseExperimentPlanOptions = {
  /** The wizard is open: fetch the site's figures so its options can offer them. */
  forWizard?: boolean;
};

export const useExperimentPlan = (experiment: Experiment, { forWizard = false }: UseExperimentPlanOptions = {}) => {
  const statisticsClient = useClient(config.STATISTICS_URL);
  const { variants } = useVariants(experiment.id);
  const { updateExperiment } = useExperiment(experiment.id);
  const { primary } = resolveObjectives(experiment);

  // Keyed on the objective too: the server measures the rate on the experiment's
  // primary objective, so changing it on the Settings tab must not reuse the old rate.
  const saved = parsePlanAssumptions(experiment.plan);
  // A saved plan built only on typed figures needs no request at all.
  const savedUsesFigures = saved !== null && (saved.dailySessions === null || saved.ratePct === null);
  const wanted = forWizard || savedUsesFigures;

  const {
    data: baseline,
    isLoading,
    isError,
  } = useQuery<PlanningBaseline>({
    enabled: wanted,
    queryKey: ['PLANNING_BASELINE', experiment.id, primary],
    queryFn: () => statisticsClient.get<PlanningBaseline>(`/${experiment.id}/planning-baseline`),
    staleTime: 1000 * 60 * 10,
  });

  // A deployed variant is a rollout, not an arm, so it takes no share of the split.
  const arms = (variants ?? []).filter((variant) => !variant.deployed);
  // Unresolvable weights (not summing to 100%) are planned as an even split rather
  // than not at all; the weights editor is where that gets fixed, not here.
  const weights = resolveWeights(arms)?.map((entry) => entry.weight) ?? arms.map(() => 1);
  // Arms that receive traffic. Two variants with a 100/0 split is not a comparison.
  const liveArms = weights.filter((weight) => weight > 0).length;

  const evaluate = (assumptions: PlanAssumptions): PlanEvaluation =>
    evaluatePlan({ assumptions, baseline: baseline ?? null, weights });

  const assumptions = saved ?? DEFAULT_PLAN;

  const savePlan = (next: PlanAssumptions | null) => updateExperiment(experiment.id, { plan: next });

  return {
    baseline: baseline ?? null,
    // A disabled query reports isLoading false, which is right: nothing is coming.
    isLoading: wanted && isLoading,
    isError,
    variantsLoading: variants === undefined,
    arms: arms.length,
    liveArms,
    primaryObjective: primary,
    saved,
    assumptions,
    current: evaluate(assumptions),
    evaluate,
    savePlan,
  };
};
